import { useCallback, useMemo } from 'react'

/**
 * Pure derived-pricing hook: everything here is computed with useMemo/
 * useCallback from data owned by useProductOrder, so totals only recompute
 * when the quantities/products they actually depend on change - same result
 * as EventDetail#calculateTotalPrice / #calculateTotalPriceEdit.
 */
const useOrderSummary = ({
  newOrderProducts,
  newOrderProductsIndividual,
  quantities,
  productOrderRender,
  productOrderRenderIndividual,
  findProductById,
}) => {
  const calculatePricePerProduct = useCallback(
    (pid, index, fallbackPrice = 0) => {
      const product = findProductById(pid, index)
      if (!product) {
        return fallbackPrice
      }

      const productRender =
        productOrderRender.find((p) => p.id === pid && p.index === index) ||
        productOrderRenderIndividual.find((p) => p.id === pid && p.index === index)

      if (productRender?.subItems?.[0]?.trackInventory) {
        return product.price
      }

      const subItemsTotal =
        product.subItems?.reduce(
          (pre, cur) => pre + cur.price * (cur.quantity ?? cur.qty ?? 1),
          0
        ) || 0
      return product.price + subItemsTotal
    },
    [findProductById, productOrderRender, productOrderRenderIndividual]
  )

  const totalPrice = useMemo(() => {
    let total = 0
    newOrderProducts?.forEach((cur) => {
      const productRender =
        productOrderRender.find((p) => p.id === cur.id && p.index === cur.index) ||
        productOrderRenderIndividual.find((p) => p.id === cur.id && p.index === cur.index)

      if (productRender?.subItems?.[0]?.trackInventory) {
        let sumTotal = 0
        cur.subItems?.forEach((sub) => {
          sumTotal += (sub.quantity ?? sub.qty ?? 1) * (cur.price + (sub.price || 0))
        })
        total += sumTotal
      } else {
        total += (cur.quantity || 0) * calculatePricePerProduct(cur.id, cur.index)
      }

      if (cur.splitCasePurchase && cur.splitCasePurchase?.enabled) {
        const splitQty = quantities[`${cur.id}-${cur.index}-split`] ?? 0
        total += splitQty * (cur.splitCasePurchase.costPerItem || 0)
      }
    })
    return total // eslint-disable-next-line max-len
  }, [newOrderProducts, quantities, calculatePricePerProduct, productOrderRender, productOrderRenderIndividual])

  // used for edit order
  const totalPriceEdit = useMemo(() => {
    let total = 0
    newOrderProducts?.forEach((cur) => {
      const productRender =
        productOrderRender.find((p) => p.id === cur.id && p.index === cur.index) ||
        productOrderRenderIndividual.find((p) => p.id === cur.id && p.index === cur.index)

      if (productRender?.subItems?.[0]?.trackInventory) {
        let sumTotal = 0
        cur.subItems?.forEach((sub) => {
          sumTotal += (sub.quantity ?? sub.qty ?? 1) * (cur.price + (sub.price || 0))
        })
        total += sumTotal
      } else {
        total += (cur.quantity || 0) * calculatePricePerProduct(cur.id, cur.index)
      }
    })
    newOrderProductsIndividual?.forEach((cur) => {
      total += (cur.quantity || 0) * cur.price
    })

    Object.keys(quantities).forEach((key) => {
      if (!key.includes('-split')) return

      const parts = key.split('-')
      if (parts.length < 3) return

      const productId = parts.slice(0, parts.length - 2).join('-')
      const index = parseInt(parts[parts.length - 2], 10)
      const splitQty = quantities[key] || 0

      const productRender =
        productOrderRender.find((p) => p.id === productId && p.index === index) ||
        productOrderRenderIndividual.find((p) => p.id === productId && p.index === index)

      let costPerItem = productRender?.splitCasePurchase?.costPerItem
      if (costPerItem == null) {
        const existingIndividualFallback = newOrderProductsIndividual.find(
          (ind) => ind.id === productId && ind.index === index
        )
        costPerItem = existingIndividualFallback?.price
      }
      if (costPerItem == null) return

      const existingIndividual = newOrderProductsIndividual.find(
        (ind) => ind.id === productId && ind.index === index
      )
      const existingQty = existingIndividual ? existingIndividual.quantity : 0

      const delta = splitQty - existingQty
      if (delta !== 0) {
        total += delta * costPerItem
      }
    })
    return total
  }, [
    newOrderProducts,
    newOrderProductsIndividual,
    quantities,
    productOrderRender,
    productOrderRenderIndividual,
    calculatePricePerProduct,
  ])

  return { calculatePricePerProduct, totalPrice, totalPriceEdit }
}

export default useOrderSummary
